import { useState, ChangeEvent, FormEvent } from 'react';
import './LicenseCheck.css';
import RiskScorePanel from './RiskScorePanel';
import { validateISOCode } from '../utils/isoValidator';
import { useECTranslation, localizeResultText } from '../i18n';
import type { LicenseResponse, SanctionsIndicator, RiskLevelColors } from '../types';

function getAuthToken(): string | null {
    try {
        const raw = localStorage.getItem('auth-storage');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed.state?.token?.access_token || null;
    } catch { return null; }
}

function LicenseCheck() {
    const { t, lang } = useECTranslation();
    const [alNumbers, setAlNumbers] = useState<string>('');
    const [destination, setDestination] = useState<string>('');
    const [destinationError, setDestinationError] = useState<string>('');
    const [countryOfOrigin, setCountryOfOrigin] = useState<string>('');
    const [countryOfOriginError, setCountryOfOriginError] = useState<string>('');
    const [shippingCountry, setShippingCountry] = useState<string>('');
    const [shippingCountryError, setShippingCountryError] = useState<string>('');
    const [sanctionsIndicator, setSanctionsIndicator] = useState<SanctionsIndicator | null>(null);
    const [result, setResult] = useState<LicenseResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Legal condition checkboxes
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

    // Transaction type
    const [isRepairReimport, setIsRepairReimport] = useState<boolean>(false);
    const [isTemporaryExhibition, setIsTemporaryExhibition] = useState<boolean>(false);
    const [isIntraGroupTransfer, setIsIntraGroupTransfer] = useState<boolean>(false);
    const [reimportWithin120Days, setReimportWithin120Days] = useState<boolean>(false);

    // Destination concerns
    const [toCustomsFreeZone, setToCustomsFreeZone] = useState<boolean>(false);

    // End-user/End-use concerns
    const [awareOfWmdUse, setAwareOfWmdUse] = useState<boolean>(false);
    const [informedByAuthority, setInformedByAuthority] = useState<boolean>(false);
    const [militaryEndUser, setMilitaryEndUser] = useState<boolean>(false);
    const [humanRightsConcern, setHumanRightsConcern] = useState<boolean>(false);

    // Helper function for risk level colors
    const getRiskLevelColor = (level: string): RiskLevelColors => {
        const colors: Record<string, RiskLevelColors> = {
            'MINIMAL': { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
            'LOW': { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
            'MODERATE': { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
            'HIGH': { bg: '#fed7aa', text: '#9a3412', border: '#f97316' },
            'CRITICAL': { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' }
        };
        return colors[level] || colors['MODERATE'];
    };

    // Check sanctions when destination country changes
    const checkSanctions = async (countryCode: string): Promise<void> => {
        if (!countryCode || countryCode.length !== 2) {
            setSanctionsIndicator(null);
            return;
        }

        try {
            const token = getAuthToken();
            const response = await fetch(`/sanctions/check/${countryCode.toUpperCase()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            });                
            if (response.ok) {
                const data = await response.json();
                setSanctionsIndicator(data);
            }
        } catch (err) {
            console.error('Failed to check sanctions:', err);
        }
    };

    // Handle destination country change with sanctions check
    const handleDestinationChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value.toUpperCase();
        setDestination(value);
        
        // Validate ISO code
        const validation = validateISOCode(value);
        setDestinationError(validation.error || '');
        
        // Check sanctions if valid
        if (value.length === 2 && !validation.error) {
            checkSanctions(value);
        } else {
            setSanctionsIndicator(null);
        }
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setResult(null);

        // Parse AL numbers from comma-separated input
        const alNumbersArray = alNumbers
            .split(',')
            .map(s => s.trim())
            .filter(s => s);

        if (alNumbersArray.length === 0 || !destination.trim()) {
            setError(t('license.errBoth'));
            setLoading(false);
            return;
        }

        // Validate AL number format: Category(0-9) + Letter(A-E) + 3 digits + optional DOTTED sub-items.
        // Underscore separators are rejected — they're an internal FAISS chunking artifact.
        const alPattern = /^[0-9][A-E]\d{3}(\.[a-zA-Z0-9]+)*$/i;
        const invalidAl = alNumbersArray.filter(al => !alPattern.test(al));
        if (invalidAl.length > 0) {
            setError(t('license.errInvalidAl', { values: invalidAl.join(', ') }));
            setLoading(false);
            return;
        }

        try {
            const token = getAuthToken();
            const response = await fetch('/determine-export-license-dual', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    al_numbers: alNumbersArray,
                    destination_country: destination.trim().toUpperCase(),
                    country_of_origin: countryOfOrigin.trim().toUpperCase() || null,
                    shipping_country: shippingCountry.trim().toUpperCase() || null,
                    // Transaction type
                    is_repair_reimport: isRepairReimport,
                    is_temporary_exhibition: isTemporaryExhibition,
                    is_intra_group_transfer: isIntraGroupTransfer,
                    reimport_within_120_days: reimportWithin120Days,
                    // Destination concerns
                    to_customs_free_zone: toCustomsFreeZone,
                    // End-user/End-use concerns
                    aware_of_wmd_use: awareOfWmdUse,
                    informed_by_authority: informedByAuthority,
                    military_end_user: militaryEndUser,
                    human_rights_concern: humanRightsConcern
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                const d = errorData?.detail;
                let msg: string;
                if (typeof d === 'string') {
                    msg = d;
                } else if (d && typeof d === 'object') {
                    // Structured 400 from the backend AL-number validator.
                    const parts: string[] = [];
                    if (d.error) parts.push(d.error);
                    if (Array.isArray(d.invalid_values) && d.invalid_values.length) {
                        parts.push(`(${d.invalid_values.join(', ')})`);
                    }
                    if (Array.isArray(d.unknown_values) && d.unknown_values.length) {
                        parts.push(`(${d.unknown_values.join(', ')})`);
                    }
                    if (d.hint) parts.push(d.hint);
                    if (d.expected_format) parts.push(`Expected: ${d.expected_format}`);
                    msg = parts.join(' — ') || t('license.errFailed');
                } else {
                    msg = t('license.errFailed');
                }
                throw new Error(msg);
            }

            const data = await response.json();
            setResult(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('common.errorOccurred'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="license-check-container">
            <h1>{t('license.title')}</h1>

            <form onSubmit={handleSubmit} className="license-form">
                {/* Country Information Section */}
                <div className="form-section">
                    <h3>{t('license.countryInfo')}</h3>
                    <div className="country-fields-grid">
                        <div className="form-group">
                            <label htmlFor="destination">
                                {t('license.destinationLabel')}
                            </label>
                            <input
                                id="destination"
                                type="text"
                                value={destination}
                                onChange={handleDestinationChange}
                                placeholder={t('license.destinationPlaceholder')}
                                className={`form-input destination-input ${destinationError ? 'input-error' : ''}`}
                                maxLength={2}
                            />
                            {destinationError ? (
                                <small style={{ color: '#dc3545', display: 'block', marginTop: '0.25rem' }}>
                                    {t('errors.invalidIso')}
                                </small>
                            ) : sanctionsIndicator && sanctionsIndicator.sanctioned ? (
                                <div className="sanctions-mini-alert">
                                    <strong>{sanctionsIndicator.country_name}</strong> - {sanctionsIndicator.level.replace('_', ' ').toUpperCase()}
                                </div>
                            ) : (
                                <small style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                    {t('common.isoHelp')}
                                </small>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="countryOfOrigin">
                                {t('license.originLabel')}
                            </label>
                            <input
                                id="countryOfOrigin"
                                type="text"
                                value={countryOfOrigin}
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase();
                                    setCountryOfOrigin(value);
                                    if (value.trim()) {
                                        const validation = validateISOCode(value);
                                        setCountryOfOriginError(validation.error || '');
                                    } else {
                                        setCountryOfOriginError('');
                                    }
                                }}
                                placeholder={t('license.originPlaceholder')}
                                className={`form-input country-origin-input ${countryOfOriginError ? 'input-error' : ''}`}
                                maxLength={2}
                            />
                            {countryOfOriginError && (
                                <small style={{ color: '#dc3545', display: 'block', marginTop: '0.25rem' }}>
                                    {t('errors.invalidIso')}
                                </small>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="shippingCountry">
                                {t('license.shippingLabel')}
                            </label>
                            <input
                                id="shippingCountry"
                                type="text"
                                value={shippingCountry}
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase();
                                    setShippingCountry(value);
                                    if (value.trim()) {
                                        const validation = validateISOCode(value);
                                        setShippingCountryError(validation.error || '');
                                    } else {
                                        setShippingCountryError('');
                                    }
                                }}
                                placeholder={t('license.shippingPlaceholder')}
                                className={`form-input shipping-country-input ${shippingCountryError ? 'input-error' : ''}`}
                                maxLength={2}
                            />
                            {shippingCountryError && (
                                <small style={{ color: '#dc3545', display: 'block', marginTop: '0.25rem' }}>
                                    {t('errors.invalidIso')}
                                </small>
                            )}
                        </div>
                    </div>
                </div>

                {/* AL Numbers Input */}
                <div className="form-section">
                    <h3>{t('license.productInfo')}</h3>
                    <div className="form-group">
                        <label htmlFor="alNumbers">
                            {t('license.alLabel')}
                        </label>
                        <textarea
                            id="alNumbers"
                            value={alNumbers}
                            onChange={(e) => setAlNumbers(e.target.value)}
                            placeholder={t('license.alPlaceholder')}
                            className="form-input"
                            rows={2}
                            style={{ resize: 'vertical' }}
                        />
                    </div>
                </div>

                {/* Legal Conditions Checkboxes */}
                <div className="form-section legal-questions">
                    <div className="section-header" onClick={() => setShowAdvanced(!showAdvanced)}>
                        <h3>{t('cond.legalConditions')}</h3>
                        <span className={`toggle-icon ${showAdvanced ? 'open' : ''}`}>▼</span>
                    </div>

                    {showAdvanced && (
                        <div className="checkbox-sections">
                            {/* Transaction Type */}
                            <div className="checkbox-group">
                                <h4>{t('cond.transactionType')}</h4>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={isRepairReimport}
                                        onChange={(e) => setIsRepairReimport(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.repair')}</strong>
                                        <small>{t('cond.repairHint')}</small>
                                    </span>
                                </label>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={isTemporaryExhibition}
                                        onChange={(e) => setIsTemporaryExhibition(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.exhibition')}</strong>
                                        <small>{t('cond.exhibitionHint')}</small>
                                    </span>
                                </label>
                                {isTemporaryExhibition && (
                                    <label className="checkbox-label nested">
                                        <input
                                            type="checkbox"
                                            checked={reimportWithin120Days}
                                            onChange={(e) => setReimportWithin120Days(e.target.checked)}
                                        />
                                        <span className="checkbox-text">
                                            <strong>{t('cond.reimport120')}</strong>
                                            <small>{t('cond.reimport120Hint')}</small>
                                        </span>
                                    </label>
                                )}
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={isIntraGroupTransfer}
                                        onChange={(e) => setIsIntraGroupTransfer(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.intraGroup')}</strong>
                                        <small>{t('cond.intraGroupHint')}</small>
                                    </span>
                                </label>
                            </div>

                            {/* Destination Concerns */}
                            <div className="checkbox-group">
                                <h4>{t('cond.destinationConcerns')}</h4>
                                <label className="checkbox-label warning">
                                    <input
                                        type="checkbox"
                                        checked={toCustomsFreeZone}
                                        onChange={(e) => setToCustomsFreeZone(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.freeZone')}</strong>
                                        <small>{t('cond.freeZoneHint')}</small>
                                    </span>
                                </label>
                            </div>

                            {/* End-User/End-Use Concerns */}
                            <div className="checkbox-group">
                                <h4>{t('cond.endUserConcerns')}</h4>
                                <label className="checkbox-label warning">
                                    <input
                                        type="checkbox"
                                        checked={informedByAuthority}
                                        onChange={(e) => setInformedByAuthority(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.informedAuthority')}</strong>
                                        <small>{t('cond.informedAuthorityHint')}</small>
                                    </span>
                                </label>
                                <label className="checkbox-label warning">
                                    <input
                                        type="checkbox"
                                        checked={awareOfWmdUse}
                                        onChange={(e) => setAwareOfWmdUse(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.wmd')}</strong>
                                        <small>{t('cond.wmdHint')}</small>
                                    </span>
                                </label>
                                <label className="checkbox-label warning">
                                    <input
                                        type="checkbox"
                                        checked={militaryEndUser}
                                        onChange={(e) => setMilitaryEndUser(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.military')}</strong>
                                        <small>{t('cond.militaryHint')}</small>
                                    </span>
                                </label>
                                <label className="checkbox-label warning">
                                    <input
                                        type="checkbox"
                                        checked={humanRightsConcern}
                                        onChange={(e) => setHumanRightsConcern(e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>{t('cond.humanRights')}</strong>
                                        <small>{t('cond.humanRightsHint')}</small>
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>

                <button
                    type="submit"
                    className="submit-button"
                    disabled={loading || !!destinationError || !!countryOfOriginError || !!shippingCountryError || !destination.trim()}
                >
                    {loading ? t('license.analyzing') : t('license.determine')}
                </button>
            </form>

            {error && (
                <div className="error-message">
                    <strong>{t('common.error')}</strong> {error}
                </div>
            )}

            {result && result.agent_result && result.rules_result ? (
                <div className="result-container">
                    <div className="result-header-dual">
                        <h2>{t('license.analysisTitle')}</h2>
                    </div>

                    <div className="dual-result-grid">
                        {/* Deterministic Rules Column - Only showing code-driven output */}
                        <div className={`result-card dual-card ${result.rules_result.license_required ? 'license-required' : 'license-not-required'}`}>
                            <div className="card-header">
                                <h3>{t('license.determination')}</h3>
                                <span className="badge rule-badge">{t('license.codeDriven')}</span>
                            </div>

                            <div className="result-item">
                                <span className="result-label">{t('license.licenseRequired')}</span>
                                <span className={`result-value ${result.rules_result.license_required ? 'required' : 'not-required'}`}>
                                    {result.rules_result.license_required ? t('common.yes') : t('common.no')}
                                </span>
                            </div>

                            {result.rules_result.authorisation && (
                                <div className="result-item">
                                    <span className="result-label">{t('license.authorisation')}</span>
                                    <span className="result-value authorisation">{result.rules_result.authorisation}</span>
                                </div>
                            )}

                            <div className="result-item full-width">
                                <span className="result-label">{t('license.legalJustification')}</span>
                                <span className="result-value reason">{localizeResultText(result.rules_result.reason, lang)}</span>
                            </div>
                        </div>

                        {/* AI Agent Column - HIDDEN as requested */}
                        {/* 
                        <div className={`result-card dual-card ${result.agent_result.license_required ? 'license-required' : 'license-not-required'}`}>
                            <div className="card-header">
                                <h3>AI Agent</h3>
                                <span className="badge agent-badge">LLM-Driven</span>
                            </div>

                            <div className="result-item">
                                <span className="result-label">License Required:</span>
                                <span className={`result-value ${result.agent_result.license_required ? 'required' : 'not-required'}`}>
                                    {result.agent_result.license_required ? 'YES' : 'NO'}
                                </span>
                            </div>

                            {result.agent_result.authorisation && (
                                <div className="result-item">
                                    <span className="result-label">Authorisation:</span>
                                    <span className="result-value authorisation">{result.agent_result.authorisation}</span>
                                </div>
                            )}

                            <div className="result-item full-width">
                                <span className="result-label">Agent Reasoning:</span>
                                <span className="result-value reason">{result.agent_result.reason}</span>
                            </div>
                        </div>
                        */}
                    </div>

                    {/* Additional RAG Explanation if available (usually in rules result enhanced by agent) */}
                    {(result.rules_result.legal_reference || result.agent_result.legal_reference) && (
                        <div className="explanation-card">
                            <h3>{t('license.legalReference')}</h3>
                            <p className="explanation-text">
                                {result.rules_result.legal_reference || result.agent_result.legal_reference}
                            </p>
                        </div>
                    )}

                    {result.risk_assessment && (
                        <div className="risk-assessment-section">
                            <h3 className="risk-section-title">{t('license.riskAssessment')}</h3>
                            
                            <div className="risk-dual-view">
                                {/* COMMENTED OUT: Unified Risk Score (1-5) - Management View
                                <div className="unified-risk-compact">
                                    <div className="unified-risk-header">
                                        <h4>Management Summary (1-5 Scale)</h4>
                                        <span className="scale-badge-small">Unified Scale</span>
                                    </div>
                                    
                                    {result.unified_risk && (
                                        <>
                                            <div 
                                                className="risk-score-compact"
                                                style={{
                                                    backgroundColor: getRiskLevelColor(result.unified_risk.risk_level).bg,
                                                    borderColor: getRiskLevelColor(result.unified_risk.risk_level).border
                                                }}
                                            >
                                                <span className="risk-icon-large">{result.unified_risk.icon}</span>
                                                <div className="risk-score-info">
                                                    <span 
                                                        className="risk-score-number-compact"
                                                        style={{ color: getRiskLevelColor(result.unified_risk.risk_level).text }}
                                                    >
                                                        {result.unified_risk.risk_score}/5
                                                    </span>
                                                    <span 
                                                        className="risk-level-label-compact"
                                                        style={{ color: getRiskLevelColor(result.unified_risk.risk_level).text }}
                                                    >
                                                        {result.unified_risk.risk_level}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <div className="risk-details-compact">
                                                <div className="risk-detail-item">
                                                    <strong>Action:</strong>
                                                    <span>{result.unified_risk.action_required}</span>
                                                </div>
                                                <div className="risk-detail-item">
                                                    <strong>Approval:</strong>
                                                    <span>{result.unified_risk.approval_level}</span>
                                                </div>
                                                {result.unified_risk.key_factors && result.unified_risk.key_factors.length > 0 && (
                                                    <div className="risk-detail-item">
                                                        <strong>Key Factors:</strong>
                                                        <ul className="key-factors-compact">
                                                            {result.unified_risk.key_factors.slice(0, 3).map((factor, idx) => (
                                                                <li key={idx}>{factor}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                                */}
                                
                                {/* Detailed Risk Score (0-100) - Now the only view */}
                                <div className="detailed-risk-compact">
                                    <RiskScorePanel riskData={result.risk_assessment} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : result && (
                <div className="result-container">
                    <h2>{t('license.resultTitle')}</h2>
                    {/* Fallback for legacy single result format if needed */}
                    <div className={`result-card ${result.license_required ? 'license-required' : 'license-not-required'}`}>
                        <div className="result-item">
                            <span className="result-label">{t('license.individualRequired')}</span>
                            <span className={`result-value ${result.license_required ? 'required' : 'not-required'}`}>
                                {result.license_required ? t('common.yes') : t('common.no')}
                            </span>
                        </div>
                        <div className="result-item full-width">
                            <span className="result-label">{t('license.reason')}</span>
                            <span className="result-value reason">{localizeResultText(result.reason, lang)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LicenseCheck;
